// OpsDashboard — admin-scoped operational visibility for the SMS pipeline.
//
// Backed by OpsAdminService (proto: combird/api/services/ui/v1/ops_admin.proto,
// Go handler: control_plane/platform/inputports/ui_api/ops_admin_handler.go).
// Reads pre-aggregated dashboard_metrics rows where view_type='ops'; no
// per-message lookups happen here. Role gating: AuthRoute isAdmin (roles
// admin / platform_admin / admin_agent / support_agent — same set the
// other AdminPanel routes use).
//
// V1 trade-offs (locked 2026-05-17, see ops_admin.proto):
//   - Latencies are AVERAGES (sum_ms/count), not p50/p99
//   - Server caps each response at 5000 rows — narrow window or filter
//     tenant if hit (banner surfaces this)
//   - Stuck-queue signal is computed client-side from the QUEUED vs
//     SUBMIT counts in the stuck_queue aggregate response
import React, {Component} from 'react';
import {connect} from 'react-redux';
import moment from 'moment';
import {
  Alert, Box, Button, ButtonGroup, Card, CardContent, Chip, CircularProgress, FormControl,
  Grid, InputLabel, MenuItem, Paper, Popover, Select, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip as MuiTooltip, Typography
} from '@mui/material';
import {CalendarTodayOutlined, InfoOutlined, Refresh, Warning} from '@mui/icons-material';
import {StaticDateRangePicker} from '@mui/x-date-pickers-pro';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';

import {adminGetOpsDashboard, adminGetStuckQueueAggregate} from '../../actions/opsDashboard';
import {getAllAgent} from '../../actions/adminPanel';
import {formatNumber} from '../../lib/utils';

// Status codes carried in dashboard_metrics.status_code. The fixed
// presentation order keeps the stacked area chart legible and the
// color palette stable across refreshes.
//
// 'ACCEPTED' is the canonical pre-submission label (2026-05-17+) — HTTP 202
// async semantics. Pre-rename rows carry 'QUEUED' (2026-05-12 → 2026-05-17)
// or 'PENDING' (pre-2026-05-12). Both legacy aliases are folded into the
// same chart series in buildTenantSummary / buildStuckBreakdown /
// getDerived below for backward-compat during the retention window.
const STATUS_CODES = [
  'ACCEPTED',
  'SUBMIT_SUCCESS',
  'SUBMIT_FAILED',
  'DELIVERED',
  'DELIVERY_FAILED',
  'EXPIRED',
  'REJECTED'
];

const STATUS_COLORS = {
  ACCEPTED: '#ffa726',
  SUBMIT_SUCCESS: '#42a5f5',
  SUBMIT_FAILED: '#ef5350',
  DELIVERED: '#66bb6a',
  DELIVERY_FAILED: '#e53935',
  EXPIRED: '#8d6e63',
  REJECTED: '#9e9e9e'
};

// Legacy aliases that should map to the 'ACCEPTED' bucket. Remove once
// retention has aged out all pre-rename rows.
const ACCEPTED_ALIASES = new Set(['ACCEPTED', 'QUEUED', 'PENDING']);

// The ops API emits DELIVERY_* status codes (dashboard_metrics rows); the
// chart series/colors use the short bucket names. Fold every synonym into
// its canonical bucket.
const DELIVERED_ALIASES = new Set(['DELIVERED', 'DELIVERY_SUCCESS']);
const REJECTED_ALIASES = new Set(['REJECTED', 'DELIVERY_REJECTED']);
const EXPIRED_ALIASES = new Set(['EXPIRED', 'DELIVERY_EXPIRED']);
const DELIVERY_FAILED_ALIASES = new Set([
  'DELIVERY_FAILED', 'DELIVERY_UNDELIVERABLE', 'DELIVERY_DELETED'
]);
const normalizeStatusCode = (code) => {
  if (ACCEPTED_ALIASES.has(code)) return 'ACCEPTED';
  if (DELIVERED_ALIASES.has(code)) return 'DELIVERED';
  if (REJECTED_ALIASES.has(code)) return 'REJECTED';
  if (EXPIRED_ALIASES.has(code)) return 'EXPIRED';
  if (DELIVERY_FAILED_ALIASES.has(code)) return 'DELIVERY_FAILED';
  return code || 'UNKNOWN';
};
// Terminal delivery outcomes (post-normalization) — used for in-flight math.
const DELIVERY_TERMINAL = new Set(['DELIVERED', 'REJECTED', 'EXPIRED', 'DELIVERY_FAILED']);

// Quick ranges for the filter bar. The selected one stays highlighted until
// the window is changed some other way (date picker).
const QUICK_RANGES = [
  {key: '1h', label: '1h', hours: 1},
  {key: '6h', label: '6h', hours: 6},
  {key: '24h', label: '24h', hours: 24},
  {key: '7d', label: '7d', hours: 24 * 7},
  {key: '30d', label: '30d', hours: 24 * 30}
];

const BUCKET_ADJECTIVE = {hour: 'Hourly', day: 'Daily', month: 'Monthly'};

const HIGHLIGHT_COLOR = '#1976d2';

const bucketTypeNoun = (bucketType) => bucketType || 'bucket';

// Latency in the largest unit that keeps it readable: ms under a second,
// then s, min, h (one decimal, trailing .0 dropped).
const formatLatency = (ms) => {
  const v = Number(ms) || 0;
  const oneDecimal = (n) => (Math.round(n * 10) / 10).toString();
  if (v < 1000) return `${Math.round(v)} ms`;
  if (v < 60 * 1000) return `${oneDecimal(v / 1000)} s`;
  if (v < 60 * 60 * 1000) return `${oneDecimal(v / 60000)} min`;
  return `${oneDecimal(v / 3600000)} h`;
};

const BUCKET_TYPES = [
  {value: 'hour', label: 'Hour'},
  {value: 'day', label: 'Day'},
  {value: 'month', label: 'Month'}
];

// SMS-type filter for the ops rollup. 'all' → no server-side filter (rows stay
// grouped by message_type). Values mirror the domain MessageType set; matched
// case-insensitively server-side.
const SMS_TYPE_OPTIONS = [
  {value: 'all', label: 'All'},
  {value: 'Transactional', label: 'Transactional'},
  {value: 'Promotional', label: 'Promotional'},
  {value: 'OTP', label: 'OTP'}
];

const STUCK_QUEUE_WINDOWS = [
  {value: 15, label: '15 min'},
  {value: 60, label: '1 hour'},
  {value: 240, label: '4 hours'},
  {value: 1440, label: '24 hours'}
];

// Server hard-cap on dashboard responses (LIMIT 5000 in SQL — see
// dashboard_metrics_ops.sql). Surface a banner when the response is at
// the cap so the user knows the time series is truncated.
const SERVER_ROW_CAP = 5000;

const TERMINAL_STATUSES = new Set(['SUBMIT_SUCCESS', 'SUBMIT_FAILED']);

// Tab indexes. Keep in lock-step with the <Tabs> children order in render().
const OVERVIEW_TAB = 0;
const LATENCY_TAB = 1;
const TENANTS_TAB = 2;
const STUCK_QUEUE_TAB = 3;

class OpsDashboard extends Component {
  constructor(props) {
    super(props);
    // Default window: last 24h, hourly buckets, fleet-wide
    const end = moment();
    const start = moment().subtract(24, 'hours');
    this.state = {
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      bucketType: 'hour',
      tenantId: '',
      messageType: 'all',
      stuckQueueWindowMinutes: 60,
      stuckQueueTenantId: '',
      loading: false,
      stuckLoading: false,
      error: null,
      stuckError: null,
      activeTab: 0,
      // Latency tab chart view: 'line' (trend), 'grouped' (4 bars/bucket to
      // compare stages within each bucket), 'stacked' (3 stage bars stacked
      // to show the E2E composition per bucket).
      latencyChartType: 'line',
      // Overview chart: 'line' (one line per status, counts read directly)
      // or 'area' (stacked, shows the volume mix).
      overviewChartType: 'line',
      // Quick range the current window came from (null once the date picker
      // sets it), and the bucket the user clicked to pin on the chart.
      quickRange: '24h',
      pinnedBucket: null,
      rangeAnchorEl: null,
      draftRange: [null, null],
      // Stuck-queue fetch is lazy — only fires the first time the user
      // opens that tab. Subsequent visits reuse cached data; Refresh in
      // the panel forces a re-fetch.
      stuckQueueFetched: false
    };
    // Memo for derived series keyed by `rows` reference. Tab switches
    // alone shouldn't re-run the per-bucket roll-ups; they only need to
    // re-render. Refs are compared by identity, so a new fetch (new
    // array) busts the cache automatically.
    this._derivedCache = null;
  }

  componentDidMount() {
    this.fetchDashboard();
    // Org names for the tenant tables. Reuse the Admin Panel's list when it
    // is already in the store; otherwise load it once.
    if (!this.props.agentList?.data?.institutes?.length) this.props.getAllAgent();
  }

  // tenantId (zitadel org id) → org name, rebuilt only when the list changes.
  orgNameById = () => {
    const institutes = this.props.agentList?.data?.institutes;
    if (this._orgNames?.source !== institutes) {
      this._orgNames = {source: institutes, byId: new Map((institutes || []).map((o) => [String(o.id), o.name]))};
    }
    return this._orgNames.byId;
  };

  // Org name on top, id below (still selectable for copy/paste into filters).
  renderTenantCell = (tenantId) => {
    const name = this.orgNameById().get(String(tenantId));
    return (
      <TableCell>
        <Typography variant="body2" fontWeight={500}>{name || 'Unknown org'}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace'}}>{tenantId}</Typography>
      </TableCell>
    );
  };

  componentDidUpdate(prevProps) {
    if (prevProps.opsDashboard !== this.props.opsDashboard) {
      this.setState({loading: false});
    }
    if (prevProps.stuckQueue !== this.props.stuckQueue) {
      this.setState({stuckLoading: false});
    }
  }

  fetchDashboard = () => {
    const {startTime, endTime, bucketType, tenantId, messageType} = this.state;
    if (!startTime || !endTime || !bucketType) {
      this.setState({error: 'start_time, end_time and bucket_type are required'});
      return;
    }
    if (!moment(endTime).isAfter(startTime)) {
      this.setState({error: 'end_time must be after start_time'});
      return;
    }
    this.setState({loading: true, error: null, pinnedBucket: null});
    this.props.adminGetOpsDashboard({startTime, endTime, bucketType, tenantId, messageType});
  };

  fetchStuckQueue = () => {
    const {stuckQueueWindowMinutes, stuckQueueTenantId} = this.state;
    this.setState({stuckLoading: true, stuckError: null});
    this.props.adminGetStuckQueueAggregate({
      windowMinutes: stuckQueueWindowMinutes,
      tenantId: stuckQueueTenantId
    });
  };

  setQuickRange = (key, hoursAgo) => () => {
    const end = moment();
    const start = moment().subtract(hoursAgo, 'hours');
    // Auto-coarsen the bucket as the window grows so the response stays
    // under the 5000-row cap. (h)our buckets over a month is the path
    // that produced the truncation incidents that motivated Option C.
    let bucketType = this.state.bucketType;
    if (hoursAgo >= 24 * 30) bucketType = 'month';
    else if (hoursAgo >= 24 * 7) bucketType = 'day';
    else if (hoursAgo > 168) bucketType = 'day';
    this.setState({
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      bucketType,
      quickRange: key
    });
  };

  openRangePicker = (e) => {
    const {startTime, endTime} = this.state;
    this.setState({
      rangeAnchorEl: e.currentTarget,
      draftRange: [moment(startTime), moment(endTime).subtract(1, 'ms')]
    });
  };

  closeRangePicker = () => this.setState({rangeAnchorEl: null});

  // Whole days, end exclusive (the API's end_time is exclusive). Windows of
  // two days or more get daily buckets, shorter ones hourly.
  applyPickedRange = () => {
    const [from, to] = this.state.draftRange;
    const start = from.clone().startOf('day');
    const end = to.clone().add(1, 'day').startOf('day');
    this.setState({
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      bucketType: end.diff(start, 'days') >= 2 ? 'day' : 'hour',
      quickRange: null,
      rangeAnchorEl: null
    }, this.fetchDashboard);
  };

  formatRangeLabel = () => {
    const {quickRange, startTime, endTime} = this.state;
    const preset = QUICK_RANGES.find((r) => r.key === quickRange);
    if (preset) return `Last ${preset.label}`;
    const start = moment(startTime);
    const end = moment(endTime);
    const wholeDays = start.isSame(start.clone().startOf('day')) && end.isSame(end.clone().startOf('day'));
    if (wholeDays) {
      return `${start.format('MMM D, YYYY')} – ${end.clone().subtract(1, 'ms').format('MMM D, YYYY')}`;
    }
    return `${start.format('MMM D, HH:mm')} – ${end.format('MMM D, HH:mm')}`;
  };

  // Clicking a bucket pins it (column marker + summary); clicking it again,
  // or outside the points, clears the pin.
  togglePinnedBucket = (e) => {
    const bucket = e && e.activeLabel != null ? Number(e.activeLabel) : null;
    this.setState((prev) => ({pinnedBucket: prev.pinnedBucket === bucket ? null : bucket}));
  };

  // Group rows into a time-series friendly shape — one chart row per
  // bucket_start with status_code counts spread across columns.
  buildTimeSeries = (rows) => {
    if (!rows || rows.length === 0) return [];
    const byBucket = new Map();
    for (const r of rows) {
      const key = r.bucketStart;
      if (!byBucket.has(key)) {
        byBucket.set(key, {bucketStart: Number(key)});
        for (const s of STATUS_CODES) byBucket.get(key)[s] = 0;
      }
      const slot = byBucket.get(key);
      // Fold legacy QUEUED/PENDING and the DELIVERY_* API codes into the
      // canonical chart buckets (the API emits DELIVERY_SUCCESS etc., the
      // series are keyed DELIVERED etc.).
      const code = normalizeStatusCode(r.statusCode);
      slot[code] = (slot[code] || 0) + Number(r.count || 0);
    }
    return Array.from(byBucket.values()).sort((a, b) => a.bucketStart - b.bucketStart);
  };

  // Average each latency stage per bucket (count-weighted). Returns one
  // row per bucket with avg_*_ms fields ready for the LineChart.
  buildLatencySeries = (rows) => {
    if (!rows || rows.length === 0) return [];
    const byBucket = new Map();
    for (const r of rows) {
      const key = r.bucketStart;
      if (!byBucket.has(key)) {
        byBucket.set(key, {
          bucketStart: Number(key),
          apiToQueueSum: 0, apiToQueueCount: 0,
          queueToSmscSum: 0, queueToSmscCount: 0,
          smscToDelSum: 0, smscToDelCount: 0,
          e2eSum: 0, e2eCount: 0
        });
      }
      const slot = byBucket.get(key);
      const count = Number(r.count || 0);
      // The backend already returned per-row averages; we re-weight by
      // count when re-aggregating multiple rows of the same bucket
      // (different tenants / sender_ids / status_codes). Rows that don't
      // carry a stage (value 0 — e.g. sent/ACCEPTED rows have no E2E)
      // must not dilute that stage's average, so weight per-stage only
      // where the stage has a value.
      const addStage = (sumKey, cntKey, v) => {
        if (v > 0 && count > 0) {
          slot[sumKey] += v * count;
          slot[cntKey] += count;
        }
      };
      addStage('apiToQueueSum', 'apiToQueueCount', Number(r.avgLatencyApiToQueueMs) || 0);
      addStage('queueToSmscSum', 'queueToSmscCount', Number(r.avgLatencyQueueToSmscMs) || 0);
      addStage('smscToDelSum', 'smscToDelCount', Number(r.avgLatencySmscToDeliveryMs) || 0);
      addStage('e2eSum', 'e2eCount', Number(r.avgLatencyE2eMs) || 0);
    }
    return Array.from(byBucket.values())
      .map((s) => ({
        bucketStart: s.bucketStart,
        avgApiToQueue: s.apiToQueueCount ? s.apiToQueueSum / s.apiToQueueCount : 0,
        avgQueueToSmsc: s.queueToSmscCount ? s.queueToSmscSum / s.queueToSmscCount : 0,
        avgSmscToDelivery: s.smscToDelCount ? s.smscToDelSum / s.smscToDelCount : 0,
        avgE2E: s.e2eCount ? s.e2eSum / s.e2eCount : 0
      }))
      .sort((a, b) => a.bucketStart - b.bucketStart);
  };

  // Per-tenant volume breakdown for the right-hand panel.
  buildTenantSummary = (rows) => {
    if (!rows || rows.length === 0) return [];
    const byTenant = new Map();
    for (const r of rows) {
      const t = r.tenantId || '(unknown)';
      if (!byTenant.has(t)) {
        byTenant.set(t, {tenantId: t, total: 0, queued: 0, success: 0, failed: 0, delivered: 0});
      }
      const slot = byTenant.get(t);
      const count = Number(r.count || 0);
      const code = normalizeStatusCode(r.statusCode);
      // total = accepted messages only. Summing every row double-counts:
      // each delivered message also has a received/DELIVERY_SUCCESS row.
      if (code === 'ACCEPTED') {
        slot.total += count;
        slot.queued += count;  // chart bucket label stays "Queued / In-flight"
      } else {
        switch (code) {
        case 'SUBMIT_SUCCESS': slot.success += count; break;
        case 'SUBMIT_FAILED': slot.failed += count; break;
        case 'DELIVERED': slot.delivered += count; break;
        case 'REJECTED':
        case 'EXPIRED':
        case 'DELIVERY_FAILED': slot.failed += count; break;
        default: break;
        }
      }
    }
    return Array.from(byTenant.values()).sort((a, b) => b.total - a.total);
  };

  // Compute per-tenant in_flight ≈ Σ ACCEPTED − Σ (SUBMIT_SUCCESS+SUBMIT_FAILED)
  // from the stuck-queue aggregate. Positive + growing → worker stalling.
  buildStuckBreakdown = (rows) => {
    if (!rows || rows.length === 0) return [];
    const byTenant = new Map();
    for (const r of rows) {
      const t = r.tenantId || '(unknown)';
      if (!byTenant.has(t)) {
        byTenant.set(t, {tenantId: t, queued: 0, terminated: 0});
      }
      const slot = byTenant.get(t);
      const count = Number(r.count || 0);
      if (ACCEPTED_ALIASES.has(r.statusCode)) {
        slot.queued += count;
      } else if (TERMINAL_STATUSES.has(r.statusCode)) {
        slot.terminated += count;
      }
    }
    return Array.from(byTenant.values())
      .map((s) => ({...s, inFlight: s.queued - s.terminated}))
      .sort((a, b) => b.inFlight - a.inFlight);
  };

  formatBucketTick = (ms) => {
    const m = moment(Number(ms));
    if (this.state.bucketType === 'hour') return m.format('HH:mm');
    if (this.state.bucketType === 'day') return m.format('MMM D');
    return m.format('MMM YY');
  };

  formatBucketFull = (ms) => moment(Number(ms)).format('YYYY-MM-DD HH:mm');

  handleTabChange = (e, value) => {
    this.setState({activeTab: value});
    // Lazy first-fetch of stuck-queue when the tab is opened. Subsequent
    // visits reuse the cached store value; the panel's Refresh button is
    // the only way to re-poll.
    if (value === STUCK_QUEUE_TAB && !this.state.stuckQueueFetched) {
      this.setState({stuckQueueFetched: true});
      this.fetchStuckQueue();
    }
  };

  // Memoised derived series + KPI roll-ups. The build_* helpers iterate
  // every row, so without memoisation tab switches would re-scan the
  // (up-to-5000-row) response on every render. Cache key = rows ref.
  getDerived(rows) {
    if (this._derivedCache && this._derivedCache.rows === rows) {
      return this._derivedCache;
    }
    // KPI semantics (per row shape — see dashboard_metrics):
    //   - direction=sent / ACCEPTED rows carry the real message count.
    //   - received / DELIVERY_* rows are per-message delivery EVENTS —
    //     summing them on top of sent double-counts every delivered msg.
    //   - received / SUBMIT_* rows carry count=0 BY DESIGN (latency-only),
    //     so submit counts cannot be derived from `count`; success is
    //     measured on delivery outcomes instead.
    let totalCount = 0;
    let totalDelivered = 0;
    let totalFailed = 0;
    let avgE2EWeightedSum = 0;
    let avgE2EWeightedCount = 0;
    for (const r of rows) {
      const c = Number(r.count || 0);
      const code = normalizeStatusCode(r.statusCode);
      if (code === 'ACCEPTED') totalCount += c;
      if (code === 'DELIVERED') totalDelivered += c;
      if (code === 'REJECTED' || code === 'EXPIRED' || code === 'DELIVERY_FAILED'
          || code === 'SUBMIT_FAILED') totalFailed += c;
      // Rows without an E2E value (sent rows, latency-only submit rows)
      // must not dilute the average.
      const e2e = Number(r.avgLatencyE2eMs) || 0;
      if (e2e > 0 && c > 0) {
        avgE2EWeightedSum += e2e * c;
        avgE2EWeightedCount += c;
      }
    }
    const totalSuccess = totalDelivered;
    // In-flight = accepted minus messages with a terminal delivery outcome.
    const totalQueued = Math.max(0, totalCount - totalDelivered - totalFailed);
    const cache = {
      rows,
      timeSeries: this.buildTimeSeries(rows),
      latencySeries: this.buildLatencySeries(rows),
      tenantSummary: this.buildTenantSummary(rows),
      kpi: {
        totalCount,
        totalQueued,
        totalSuccess,
        totalFailed,
        avgE2E: avgE2EWeightedCount ? avgE2EWeightedSum / avgE2EWeightedCount : 0,
        successRate: (totalSuccess + totalFailed) > 0
          ? (totalSuccess / (totalSuccess + totalFailed)) * 100
          : 0
      }
    };
    this._derivedCache = cache;
    return cache;
  }

  // Hover: dashed guide line + enlarged dots, tooltip lists every status.
  // Click: pins the bucket (solid marker) and shows its counts below.
  renderOverviewChart = (timeSeries) => {
    const {overviewChartType, pinnedBucket} = this.state;
    const ChartComponent = overviewChartType === 'area' ? AreaChart : LineChart;
    const pinned = timeSeries.find((r) => r.bucketStart === pinnedBucket);
    return (
      <>
        <Box sx={{width: '100%', height: 300, cursor: 'pointer'}}>
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent data={timeSeries} margin={{top: 8, right: 16, left: 8, bottom: 4}} onClick={this.togglePinnedBucket}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis dataKey="bucketStart" tickFormatter={this.formatBucketTick} tick={{fontSize: 11}} />
              <YAxis tickFormatter={formatNumber} tick={{fontSize: 11}} width={48} />
              <Tooltip
                labelFormatter={this.formatBucketFull}
                formatter={(value) => formatNumber(value)}
                contentStyle={{fontSize: 12}}
                cursor={{stroke: HIGHLIGHT_COLOR, strokeWidth: 1, strokeDasharray: '4 4'}}
              />
              <Legend wrapperStyle={{fontSize: 11}} iconSize={10} />
              {pinned && <ReferenceLine x={pinnedBucket} stroke={HIGHLIGHT_COLOR} strokeWidth={2} />}
              {STATUS_CODES.map((s) => (overviewChartType === 'area' ? (
                <Area
                  key={s}
                  type="monotone"
                  dataKey={s}
                  stackId="1"
                  stroke={STATUS_COLORS[s]}
                  fill={STATUS_COLORS[s]}
                  activeDot={{r: 5}}
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  key={s}
                  type="monotone"
                  dataKey={s}
                  stroke={STATUS_COLORS[s]}
                  strokeWidth={2}
                  dot={{r: 3}}
                  activeDot={{r: 6}}
                  isAnimationActive={false}
                />
              )))}
            </ChartComponent>
          </ResponsiveContainer>
        </Box>
        {pinned ? (
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{mt: 1}}>
            <Typography variant="body2" fontWeight={600} sx={{color: HIGHLIGHT_COLOR}}>
              {this.formatBucketFull(pinned.bucketStart)}
            </Typography>
            {STATUS_CODES.filter((s) => pinned[s] > 0).map((s) => (
              <Chip
                key={s}
                size="small"
                variant="outlined"
                label={`${s}: ${formatNumber(pinned[s])}`}
                sx={{borderColor: STATUS_COLORS[s], color: STATUS_COLORS[s]}}
              />
            ))}
            <Button size="small" onClick={() => this.setState({pinnedBucket: null})}>Clear</Button>
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
            Click a point to pin that {bucketTypeNoun(this.state.bucketType)}.
          </Typography>
        )}
      </>
    );
  };

  render() {
    const {opsDashboard, stuckQueue} = this.props;
    const {bucketType, tenantId, messageType,
      stuckQueueWindowMinutes, stuckQueueTenantId,
      loading, stuckLoading, error, stuckError, activeTab, latencyChartType,
      overviewChartType, quickRange, rangeAnchorEl, draftRange} = this.state;

    const rows = opsDashboard?.rows || [];
    const stuckRows = stuckQueue?.rows || [];
    const truncated = rows.length >= SERVER_ROW_CAP;

    const {timeSeries, latencySeries, tenantSummary, kpi} = this.getDerived(rows);
    const stuckBreakdown = this.buildStuckBreakdown(stuckRows);
    const {totalCount, totalQueued, totalSuccess, totalFailed, avgE2E, successRate} = kpi;

    return (
      <Box sx={{p: 2}}>
        {/* ── Compact title row — long intro lives in the info tooltip ── */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.5}}>
          <Typography variant="h6">Ops Dashboard</Typography>
          <MuiTooltip
            title={
              "Pre-aggregated ClickHouse view (view_type='ops'). Latency values "
              + 'are averages per stage (sum_ms/count). Server caps each response at '
              + `${SERVER_ROW_CAP.toLocaleString()} rows.`
            }
            arrow
          >
            <InfoOutlined fontSize="small" sx={{color: 'text.secondary', cursor: 'help'}} />
          </MuiTooltip>
        </Stack>

        {/* ── Filters — single compact row, no helperText (tooltips instead) ── */}
        <Paper sx={{p: 1.25, mb: 1.5}}>
          <Stack direction={{xs: 'column', md: 'row'}} spacing={1} alignItems={{md: 'center'}}>
            <Button
              variant="outlined"
              onClick={this.openRangePicker}
              startIcon={<CalendarTodayOutlined fontSize="small" />}
              sx={{
                textTransform: 'none', justifyContent: 'flex-start', minWidth: 250, flex: 1,
                borderColor: 'divider', color: 'text.primary', '&:hover': {borderColor: 'primary.main'}
              }}
            >
              <Typography variant="body2" fontWeight={500} noWrap>{this.formatRangeLabel()}</Typography>
            </Button>
            <Popover
              open={Boolean(rangeAnchorEl)}
              anchorEl={rangeAnchorEl}
              onClose={this.closeRangePicker}
              anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
              transformOrigin={{vertical: 'top', horizontal: 'left'}}
              PaperProps={{elevation: 8, sx: {borderRadius: 2, mt: 1}}}
            >
              <StaticDateRangePicker
                value={draftRange}
                onChange={(range) => this.setState({draftRange: range})}
                disableFuture
                displayStaticWrapperAs="desktop"
                calendars={window.isMobile() ? 1 : 2}
                slots={{
                  actionBar: () => (
                    <Stack direction="row" spacing={2} sx={{gridRow: 3, gridColumn: 2, justifySelf: 'end', p: 2, pt: 0}}>
                      <Button size="small" onClick={this.closeRangePicker}>Cancel</Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={this.applyPickedRange}
                        disabled={!draftRange[0] || !draftRange[1]}
                      >
                        Apply
                      </Button>
                    </Stack>
                  )
                }}
              />
            </Popover>
            <FormControl size="small" sx={{minWidth: 110}}>
              <InputLabel>Bucket</InputLabel>
              <Select
                label="Bucket"
                value={bucketType}
                onChange={(e) => this.setState({bucketType: e.target.value})}
              >
                {BUCKET_TYPES.map((b) => (
                  <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{minWidth: 150}}>
              <InputLabel>SMS Type</InputLabel>
              <Select
                label="SMS Type"
                value={messageType}
                onChange={(e) => this.setState({messageType: e.target.value})}
              >
                {SMS_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Tenant ID"
              placeholder="empty → fleet-wide"
              value={tenantId}
              onChange={(e) => this.setState({tenantId: e.target.value})}
              sx={{minWidth: 180, flex: 1}}
            />
            <ButtonGroup size="small" variant="outlined">
              {QUICK_RANGES.map((r) => (
                <Button
                  key={r.key}
                  variant={quickRange === r.key ? 'contained' : 'outlined'}
                  onClick={this.setQuickRange(r.key, r.hours)}
                >
                  {r.label}
                </Button>
              ))}
            </ButtonGroup>
            <Button
              size="small"
              variant="contained"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Refresh fontSize="small" />}
              onClick={this.fetchDashboard}
              disabled={loading}
              sx={{whiteSpace: 'nowrap', minWidth: 90}}
            >
              {loading ? '…' : 'Apply'}
            </Button>
          </Stack>
        </Paper>

        {/* ── Errors / cap banner ────────────────────────────────── */}
        {error && <Alert severity="error" sx={{mb: 1.5}}>{error}</Alert>}
        {opsDashboard?.error && <Alert severity="error" sx={{mb: 1.5}}>{opsDashboard.error}</Alert>}
        {truncated && (
          <Alert severity="warning" sx={{mb: 1.5, py: 0}} icon={<Warning fontSize="inherit" />}>
            Response hit the {SERVER_ROW_CAP.toLocaleString()}-row server cap — narrow the window or filter by tenant.
          </Alert>
        )}

        {/* ── KPI strip (compact — value-led, label inline) ─────── */}
        <Grid container spacing={1} sx={{mb: 1.5}}>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{p: 1.25, '&:last-child': {pb: 1.25}}}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{lineHeight: 1.2}}>
                  Total messages
                </Typography>
                <Typography variant="h6" sx={{lineHeight: 1.2}}>{formatNumber(totalCount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{p: 1.25, '&:last-child': {pb: 1.25}}}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{lineHeight: 1.2}}>
                  Queued (in flight)
                </Typography>
                <Typography variant="h6" sx={{lineHeight: 1.2}} color={totalQueued > 0 ? 'warning.main' : 'text.primary'}>
                  {formatNumber(totalQueued)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{p: 1.25, '&:last-child': {pb: 1.25}}}>
                <MuiTooltip title="Delivered / (delivered + failed delivery outcomes). Submit-level counts aren't exposed by the rollup (SUBMIT rows are latency-only)." arrow>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{lineHeight: 1.2, cursor: 'help'}}>
                    Delivery success
                  </Typography>
                </MuiTooltip>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="h6" sx={{lineHeight: 1.2}}>{successRate.toFixed(1)}%</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(totalSuccess)} / {formatNumber(totalSuccess + totalFailed)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{p: 1.25, '&:last-child': {pb: 1.25}}}>
                <MuiTooltip title="Average end-to-end latency (avg, not p50/p99)" arrow>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{lineHeight: 1.2, cursor: 'help'}}>
                    Avg E2E latency
                  </Typography>
                </MuiTooltip>
                <Typography variant="h6" sx={{lineHeight: 1.2}}>{avgE2E ? formatLatency(avgE2E) : '—'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Tabs nav ──────────────────────────────────────────── */}
        <Paper sx={{mb: 2}}>
          <Tabs
            value={activeTab}
            onChange={this.handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Latency" />
            <Tab label="Tenants" />
            <Tab label="Stuck queues" />
          </Tabs>
        </Paper>

        {/* ── Overview tab — Volume by status ───────────────────── */}
        {activeTab === OVERVIEW_TAB && (
          <Paper sx={{p: 2}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1, flexWrap: 'wrap', gap: 1}}>
              <Typography variant="subtitle1">
                Message count by status — {BUCKET_ADJECTIVE[bucketType] || bucketType}
              </Typography>
              <ButtonGroup size="small" variant="outlined">
                <Button variant={overviewChartType === 'line' ? 'contained' : 'outlined'} onClick={() => this.setState({overviewChartType: 'line'})}>Line</Button>
                <Button variant={overviewChartType === 'area' ? 'contained' : 'outlined'} onClick={() => this.setState({overviewChartType: 'area'})}>Stacked area</Button>
              </ButtonGroup>
            </Stack>
            {timeSeries.length === 0 ? (
              <Box sx={{py: 6, textAlign: 'center', color: 'text.secondary'}}>
                {loading ? 'Loading…' : 'No data for this window.'}
              </Box>
            ) : this.renderOverviewChart(timeSeries)}
          </Paper>
        )}

        {/* ── Latency tab ───────────────────────────────────────── */}
        {activeTab === LATENCY_TAB && (
          <Paper sx={{p: 2}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1, flexWrap: 'wrap', gap: 1}}>
              <Typography variant="subtitle1">
                Average latency per stage
              </Typography>
              {/* Line = trend over time; Grouped = 4 bars per bucket to compare
                  stages within a bucket; Stacked = 3 stages composing E2E. */}
              <ButtonGroup size="small" variant="outlined">
                <Button variant={latencyChartType === 'line' ? 'contained' : 'outlined'} onClick={() => this.setState({latencyChartType: 'line'})}>Line</Button>
                <Button variant={latencyChartType === 'grouped' ? 'contained' : 'outlined'} onClick={() => this.setState({latencyChartType: 'grouped'})}>Grouped bars</Button>
                <Button variant={latencyChartType === 'stacked' ? 'contained' : 'outlined'} onClick={() => this.setState({latencyChartType: 'stacked'})}>Stacked bars</Button>
              </ButtonGroup>
            </Stack>
            {latencyChartType === 'stacked' && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1}}>
                Stacked stages (API→Queue + Queue→SMSC + SMSC→Delivery) compose the end-to-end latency per {bucketType} bucket.
              </Typography>
            )}
            {latencySeries.length === 0 ? (
              <Box sx={{py: 6, textAlign: 'center', color: 'text.secondary'}}>
                {loading ? 'Loading…' : 'No data for this window.'}
              </Box>
            ) : (
              <Box sx={{width: '100%', height: 300}}>
                <ResponsiveContainer width="100%" height="100%">
                  {latencyChartType === 'line' ? (
                    <LineChart data={latencySeries} margin={{top: 8, right: 16, left: 8, bottom: 4}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                      <XAxis dataKey="bucketStart" tickFormatter={this.formatBucketTick} tick={{fontSize: 11}} />
                      <YAxis tickFormatter={formatLatency} tick={{fontSize: 11}} width={64} />
                      <Tooltip labelFormatter={this.formatBucketFull} formatter={(value) => formatLatency(value)} contentStyle={{fontSize: 12}} />
                      <Legend wrapperStyle={{fontSize: 11}} iconSize={10} />
                      <Line type="monotone" dataKey="avgApiToQueue" name="API→Queue" stroke="#8884d8" dot={false} isAnimationActive={false} />
                      <Line type="monotone" dataKey="avgQueueToSmsc" name="Queue→SMSC" stroke="#82ca9d" dot={false} isAnimationActive={false} />
                      <Line type="monotone" dataKey="avgSmscToDelivery" name="SMSC→Delivery" stroke="#ffc658" dot={false} isAnimationActive={false} />
                      <Line type="monotone" dataKey="avgE2E" name="E2E" stroke="#ef5350" dot={false} strokeWidth={2} isAnimationActive={false} />
                    </LineChart>
                  ) : (
                    <BarChart data={latencySeries} margin={{top: 8, right: 16, left: 8, bottom: 4}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                      <XAxis dataKey="bucketStart" tickFormatter={this.formatBucketTick} tick={{fontSize: 11}} />
                      <YAxis tickFormatter={formatLatency} tick={{fontSize: 11}} width={64} />
                      <Tooltip labelFormatter={this.formatBucketFull} formatter={(value) => formatLatency(value)} contentStyle={{fontSize: 12}} />
                      <Legend wrapperStyle={{fontSize: 11}} iconSize={10} />
                      {latencyChartType === 'stacked' ? (
                        [
                          <Bar key="a2q" dataKey="avgApiToQueue" name="API→Queue" stackId="lat" fill="#8884d8" isAnimationActive={false} />,
                          <Bar key="q2s" dataKey="avgQueueToSmsc" name="Queue→SMSC" stackId="lat" fill="#82ca9d" isAnimationActive={false} />,
                          <Bar key="s2d" dataKey="avgSmscToDelivery" name="SMSC→Delivery" stackId="lat" fill="#ffc658" isAnimationActive={false} />
                        ]
                      ) : (
                        [
                          <Bar key="a2q" dataKey="avgApiToQueue" name="API→Queue" fill="#8884d8" isAnimationActive={false} />,
                          <Bar key="q2s" dataKey="avgQueueToSmsc" name="Queue→SMSC" fill="#82ca9d" isAnimationActive={false} />,
                          <Bar key="s2d" dataKey="avgSmscToDelivery" name="SMSC→Delivery" fill="#ffc658" isAnimationActive={false} />,
                          <Bar key="e2e" dataKey="avgE2E" name="E2E" fill="#ef5350" isAnimationActive={false} />
                        ]
                      )}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        )}

        {/* ── Tenants tab — top-100 (was top-50 pre-tabs) ─────── */}
        {activeTab === TENANTS_TAB && (
          <Paper sx={{p: 2}}>
            <Typography variant="subtitle1" sx={{mb: 1}}>
              Top tenants in window (by volume)
            </Typography>
            {tenantSummary.length === 0 ? (
              <Box sx={{py: 4, textAlign: 'center', color: 'text.secondary'}}>
                {loading ? 'Loading…' : 'No data.'}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenant</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Queued</TableCell>
                      <TableCell align="right">Submit OK</TableCell>
                      <TableCell align="right">Submit Failed</TableCell>
                      <TableCell align="right">Delivered</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenantSummary.slice(0, 100).map((t) => (
                      <TableRow key={t.tenantId} hover>
                        {this.renderTenantCell(t.tenantId)}
                        <TableCell align="right">{formatNumber(t.total)}</TableCell>
                        <TableCell align="right">{formatNumber(t.queued)}</TableCell>
                        <TableCell align="right">{formatNumber(t.success)}</TableCell>
                        <TableCell align="right">{formatNumber(t.failed)}</TableCell>
                        <TableCell align="right">{formatNumber(t.delivered)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* ── Stuck-queue tab — own filters + own API (lazy) ───── */}
        {activeTab === STUCK_QUEUE_TAB && (
          <Paper sx={{p: 2}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1}}>
              <Box>
                <Typography variant="subtitle1">Stuck-queue health</Typography>
                <Typography variant="caption" color="text.secondary">
                  In-flight = Σ ACCEPTED − Σ (SUBMIT_SUCCESS + SUBMIT_FAILED). Positive + growing → worker stalling.
                </Typography>
              </Box>
              <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                <FormControl size="small" sx={{minWidth: 120}}>
                  <InputLabel>Window</InputLabel>
                  <Select
                    label="Window"
                    value={stuckQueueWindowMinutes}
                    onChange={(e) => this.setState({stuckQueueWindowMinutes: e.target.value})}
                  >
                    {STUCK_QUEUE_WINDOWS.map((w) => (
                      <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Tenant ID"
                  value={stuckQueueTenantId}
                  onChange={(e) => this.setState({stuckQueueTenantId: e.target.value})}
                  sx={{minWidth: 180}}
                />
                <Button
                  variant="outlined"
                  startIcon={stuckLoading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                  onClick={this.fetchStuckQueue}
                  disabled={stuckLoading}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
            {stuckError && <Alert severity="error" sx={{mb: 2}}>{stuckError}</Alert>}
            {stuckQueue?.error && <Alert severity="error" sx={{mb: 2}}>{stuckQueue.error}</Alert>}
            {stuckBreakdown.length === 0 ? (
              <Box sx={{py: 4, textAlign: 'center', color: 'text.secondary'}}>
                {stuckLoading ? 'Loading…' : 'No data.'}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenant</TableCell>
                      <TableCell align="right">Queued</TableCell>
                      <TableCell align="right">Submit (ok+failed)</TableCell>
                      <TableCell align="right">In-flight</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stuckBreakdown.slice(0, 100).map((t) => (
                      <TableRow key={t.tenantId} hover>
                        {this.renderTenantCell(t.tenantId)}
                        <TableCell align="right">{formatNumber(t.queued)}</TableCell>
                        <TableCell align="right">{formatNumber(t.terminated)}</TableCell>
                        <TableCell align="right" sx={{
                          color: t.inFlight > 0 ? 'warning.main' : 'text.primary',
                          fontWeight: t.inFlight > 0 ? 600 : 400
                        }}>
                          {formatNumber(t.inFlight)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  opsDashboard: state.opsDashboard?.opsDashboard,
  stuckQueue: state.opsDashboard?.stuckQueue,
  agentList: state.adminPanel?.agentResponse
});

export default connect(mapStateToProps, {
  adminGetOpsDashboard,
  adminGetStuckQueueAggregate,
  getAllAgent
})(OpsDashboard);
